import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ChevronLeft, Calendar, User, Tag, Share2, Facebook, Twitter, Linkedin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import BlogCard from "../components/BlogCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [topCasinos, setTopCasinos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [blogRes, allBlogsRes] = await Promise.all([
          axios.get(`${API}/blogs/${slug}`),
          axios.get(`${API}/blogs`),
          axios.get(`${API}/casinos`),
        ]);
        setBlog(blogRes.data);
        // Get related blogs from same category
        const related = allBlogsRes.data
          .filter(b => b.slug !== slug)
          .slice(0, 3);
        setRelatedBlogs(related);
        const casinosRes = await Promise.all([axios.get(`${API}/casinos`)]);
        setTopCasinos(casinosRes[0].data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching blog:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  useEffect(() => {
    if (!blog) return;
    document.title = `${blog.seo_title || blog.title} | PlaySpiro`;
    const setMeta = (name, val, prop) => {
      let el = document.querySelector(prop ? `meta[property="${name}"]` : `meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); prop ? el.setAttribute('property', name) : el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', val);
    };
    setMeta('description', blog.seo_description || blog.excerpt);
    setMeta('og:title', blog.seo_title || blog.title, true);
    setMeta('og:description', blog.excerpt, true);
    setMeta('og:url', `https://playspiro.com/blog/${blog.slug}`, true);
    setMeta('og:type', 'article', true);
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": blog.title,
      "description": blog.excerpt,
      "author": {"@type": "Organization", "name": "PlaySpiro"},
      "publisher": {"@type": "Organization", "name": "PlaySpiro", "url": "https://playspiro.com"},
      "datePublished": blog.created_at || new Date().toISOString(),
      "dateModified": blog.updated_at || new Date().toISOString(),
      "mainEntityOfPage": {"@type": "WebPage", "@id": `https://playspiro.com/blog/${blog.slug}`},
      "keywords": (blog.tags || []).join(', ')
    };
    let script = document.querySelector('#blog-jsonld');
    if (!script) { script = document.createElement('script'); script.id = 'blog-jsonld'; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(jsonLd);
    return () => { const s = document.querySelector('#blog-jsonld'); if (s) s.remove(); };
  }, [blog]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatContent = (content) => {
    // Simple markdown-like formatting
    return content
      .split('\n\n')
      .map((para, i) => {
        if (para.startsWith('# ')) {
          return <h1 key={i} className="text-3xl font-bold text-white mb-4 mt-8 font-['Montserrat']">{para.slice(2)}</h1>;
        }
        if (para.startsWith('## ')) {
          return <h2 key={i} className="text-2xl font-bold text-white mb-3 mt-6 font-['Montserrat']">{para.slice(3)}</h2>;
        }
        if (para.startsWith('### ')) {
          return <h3 key={i} className="text-xl font-bold text-white mb-2 mt-4 font-['Montserrat']">{para.slice(4)}</h3>;
        }
        if (para.startsWith('- ')) {
          const items = para.split('\n').filter(line => line.startsWith('- '));
          return (
            <ul key={i} className="list-disc list-inside space-y-2 text-white/80 mb-4 ml-4">
              {items.map((item, j) => (
                <li key={j}>{item.slice(2)}</li>
              ))}
            </ul>
          );
        }
        if (para.startsWith('**') && para.endsWith('**')) {
          return <p key={i} className="text-white font-bold mb-4">{para.slice(2, -2)}</p>;
        }
        return <p key={i} className="text-white/80 leading-relaxed mb-4">{para}</p>;
      });
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="h-96 bg-card rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen py-8 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Article Not Found</h1>
          <Link to="/blog">
            <Button className="gradient-neon text-white rounded-full">
              <ChevronLeft className="w-4 h-4 mr-2" /> Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog ? `${blog.title} – PlaySpiro` : "Blog – PlaySpiro"}</title>
        <meta name="description" content={blog ? blog.excerpt || blog.title : "Read casino guides at PlaySpiro."} />
        <link rel="canonical" href={`https://playspiro.com/blog/${blog?.slug || ""}`} />
        <meta property="og:title" content={blog ? `${blog.title} – PlaySpiro` : "Blog – PlaySpiro"} />
        <meta property="og:url" content={`https://playspiro.com/blog/${blog?.slug || ""}`} />
        <meta property="og:type" content="article" />
        {blog?.image && <meta property="og:image" content={blog.image} />}
      </Helmet>
    <div className="min-h-screen py-8" data-testid="blog-post-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <Link to="/blog" className="inline-flex items-center text-white/60 hover:text-primary transition-colors">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Blog
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <article className="lg:col-span-2">
            {/* Header */}
            <header className="mb-8">
          <Badge className="mb-4 bg-primary/20 text-primary">{blog.category}</Badge>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 font-['Montserrat']">
            {blog.title}
          </h1>
          <p className="text-xl text-white/60 mb-6">{blog.excerpt}</p>
          
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-white/50 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{blog.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(blog.created_at)}</span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className="relative rounded-2xl overflow-hidden mb-8 aspect-video">
          <img
            src={blog.featured_image}
            alt={blog.title}
            className="w-full h-full object-cover"
           loading="lazy"/>
        </div>

        {/* Inline Ad */}
        <div className="ad-banner h-[90px] rounded-xl mb-8" data-testid="ad-blog-post-inline">
          <span>Ad Space 728x90</span>
        </div>

        {/* Content */}
        <div className="blog-content mb-12" dangerouslySetInnerHTML={{__html: blog.content}}/>

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="flex items-center gap-3 mb-8 pb-8 border-b border-white/10">
            <Tag className="w-4 h-4 text-white/40" />
            <div className="flex flex-wrap gap-2">
              {blog.tags.map((tag, i) => (
                <Badge key={i} variant="secondary" className="bg-white/10 text-white">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Share */}
        <div className="flex items-center gap-4 mb-12">
          <span className="text-white/60 flex items-center gap-2">
            <Share2 className="w-4 h-4" /> Share:
          </span>
          <div className="flex gap-2">
            <button className="w-10 h-10 rounded-full bg-blue-600/20 text-blue-400 flex items-center justify-center hover:bg-blue-600/30 transition-colors">
              <Facebook className="w-5 h-5" />
            </button>
            <button className="w-10 h-10 rounded-full bg-sky-500/20 text-sky-400 flex items-center justify-center hover:bg-sky-500/30 transition-colors">
              <Twitter className="w-5 h-5" />
            </button>
            <button className="w-10 h-10 rounded-full bg-blue-700/20 text-blue-500 flex items-center justify-center hover:bg-blue-700/30 transition-colors">
              <Linkedin className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Related Articles */}
        {relatedBlogs.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-white mb-6 font-['Montserrat']">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedBlogs.map((relatedBlog) => (
                <BlogCard key={relatedBlog.id} blog={relatedBlog} />
              ))}
            </div>
          </section>
        )}

          </article>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-1">

            {/* Top Casinos */}
            {topCasinos.length > 0 && (
              <div className="glass rounded-2xl p-5">
                <h3 className="text-lg font-bold text-white mb-4 font-['Montserrat']">Top Casinos</h3>
                <div className="space-y-3">
                  {topCasinos.map(c => (
                    <Link key={c.id} to={`/casino/${c.slug}`} className="flex items-center gap-3 group">
                      <img src={c.logo} alt={c.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white/80 text-sm group-hover:text-primary transition-colors truncate font-semibold">{c.name}</p>
                        <p className="text-secondary text-xs">⭐ {c.rating}/5</p>
                        <p className="text-white/40 text-xs line-clamp-1">{c.bonus_description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link to="/casinos" className="text-primary text-xs mt-4 inline-block hover:underline">View all casinos →</Link>
              </div>
            )}

            {/* Bonuses CTA */}
            <div className="glass rounded-2xl p-5 text-center border border-secondary/20">
              <p className="text-secondary font-bold mb-1">🎁 Exclusive Bonuses</p>
              <p className="text-white/50 text-xs mb-4">Free spins & no deposit offers</p>
              <Link to="/bonuses">
                <button className="w-full gradient-neon text-white text-sm font-bold py-2 rounded-full">
                  Claim Bonus →
                </button>
              </Link>
            </div>

            {/* Related Articles */}
            {relatedBlogs.length > 0 && (
              <div className="glass rounded-2xl p-5">
                <h3 className="text-lg font-bold text-white mb-4 font-['Montserrat']">Related Articles</h3>
                <div className="space-y-3">
                  {relatedBlogs.map((relatedBlog) => (
                    <Link key={relatedBlog.id} to={`/blog/${relatedBlog.slug}`} className="block group">
                      <p className="text-white/80 text-sm group-hover:text-primary transition-colors line-clamp-2">{relatedBlog.title}</p>
                      <span className="text-white/40 text-xs">Read article →</span>
                    </Link>
                  ))}
                </div>
                <Link to="/blog" className="text-primary text-xs mt-4 inline-block hover:underline">All articles →</Link>
              </div>
            )}

            {/* Ad */}
            <div className="ad-banner h-[250px] rounded-xl">
              <span>Ad 300x250</span>
            </div>

          </aside>
        </div>
      </div>
    </div>
    </>
  );
}